import { useApolloClient } from '@apollo/client';
import { useHubble } from '@seek/hubble';
import { metrics } from '@seek/metrics-js';
import { useTranslations } from '@vocab/react';
import { Stack, Text, Box } from 'braid-design-system';
import lottie from 'lottie-web';
import { useEffect, useState, useRef } from 'react';

import { useAppConfig } from 'src/modules/appConfig';

import animationData from '../../../Resume/EditResume/FileUpload/animation.json'; // TODO: move animation related assets and config to a shared location
import translations from '../../AvatarContainer/.vocab';
import { useCreateProfileAvatarMutation, uploadProfileAvatar } from '../data';
import { usePollAvatarStatus } from '../usePollAvatarStatus';

import { UploadError } from './UploadError';
import * as styles from './UploadingStep.css.ts';
import {
  AnimationState,
  AnimationTimings,
  playAnimation,
  UploadState,
  UploadProgressStates,
} from './avatarUploadUtils';
import { runAvatarAnimationLifecycle } from './runAvatarAnimationLifecycle';

interface Props {
  closeDialog: () => void;
  croppedImage: File | null;
  onUploadStepEnd: () => void;
  hasError: React.MutableRefObject<boolean>;
}

// Keep this outside to avoid duplicate tracking on remounts. Set is used to avoid duplicates.
const trackedAvatarUploadIds = new Set<string>();

// Module-level so we only count upload error once per attempt even if the component remounts (e.g. Strict Mode).
// Reset when a new upload starts (avatar_upload_started).
let hasTrackedUploadError = false;

export const UploadingStep = ({
  croppedImage,
  closeDialog,
  onUploadStepEnd,
  hasError,
}: Props) => {
  const { t } = useTranslations(translations);
  const apolloClient = useApolloClient();
  const hubble = useHubble();
  const { zone, language } = useAppConfig();
  const [uploadError, setUploadError] = useState<boolean>(false);
  // Track if upload has been initiated to prevent re-triggering
  const hasInitiatedUploadRef = useRef(false);
  // Store timeout ID so it can be cleared in cleanup if component unmounts or effect re-runs
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Animation state
  const [animationState, setAnimationState] = useState<AnimationState | null>(
    null,
  );
  const [uploadProgressLabel, setUploadProgressLabel] =
    useState<UploadState | null>(null);
  const animationRef = useRef<HTMLDivElement>(null);
  const animationInstance = useRef<any>(null);
  const animationStartedRef = useRef<boolean>(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  // Track if polling has ever started to detect unexpected polling failures
  const hasStartedPollingRef = useRef<boolean>(false);

  const {
    createProfileAvatarInput,
    createProfileAvatarResponse,
    createProfileAvatarError,
  } = useCreateProfileAvatarMutation();
  const {
    status: pollingStatus,
    startPolling,
    timeout: pollingTimeout,
    isPolling,
    profileAvatarStatusFailureError,
  } = usePollAvatarStatus();

  // Step 1: Create avatar when file is selected
  useEffect(() => {
    hasTrackedUploadError = false;

    if (!croppedImage || createProfileAvatarResponse) {
      return;
    }

    metrics.count('avatar_upload_started');

    createProfileAvatarInput();
  }, [croppedImage, createProfileAvatarResponse, createProfileAvatarInput]);

  // Step 2: Handle create avatar response and upload file when we have avatarId and uploadUrl
  useEffect(() => {
    if (!createProfileAvatarResponse) {
      return;
    }

    // If avatar creation failed, exit early (error will be shown via isError calculation)
    if (createProfileAvatarError) {
      onUploadStepEnd();
      return;
    }

    // If avatar created successfully, upload to S3
    if (
      createProfileAvatarResponse.__typename === 'CreateProfileAvatarSuccess' &&
      !hasInitiatedUploadRef.current &&
      !isPolling
    ) {
      hasInitiatedUploadRef.current = true;

      // Upload file to S3
      uploadProfileAvatar(croppedImage!, createProfileAvatarResponse.uploadUrl)
        .then(() => {
          startPolling(createProfileAvatarResponse.avatarId);
        })
        .catch(() => {
          setUploadError(true);
          onUploadStepEnd();
        });
    }
  }, [
    createProfileAvatarResponse,
    createProfileAvatarError,
    isPolling,
    croppedImage,
    startPolling,
    onUploadStepEnd,
  ]);

  // Track when polling has started
  useEffect(() => {
    if (isPolling) {
      hasStartedPollingRef.current = true;
    }
  }, [isPolling]);

  // Step 3: Handle polling status updates
  useEffect(() => {
    if (pollingStatus === 'ProfileAvatarStatusSuccess') {
      // Only set timeout if we haven't already set it
      if (!successTimeoutRef.current) {
        const avatarId =
          createProfileAvatarResponse?.__typename ===
          'CreateProfileAvatarSuccess'
            ? createProfileAvatarResponse.avatarId
            : undefined;

        if (avatarId && !trackedAvatarUploadIds.has(avatarId)) {
          trackedAvatarUploadIds.add(avatarId);

          metrics.count('avatar_upload_success');

          hubble.trackEvent('profile_update_succeeded', {
            updateSection: 'profile avatar',
            updateType: 'add',
            updateValues: [],
            displayLanguage: language,
            zone,
          });
        }
        // Close dialog after a brief delay to show success message
        apolloClient.refetchQueries({ include: ['GetProfileAvatar'] });
        successTimeoutRef.current = setTimeout(() => {
          closeDialog();
          successTimeoutRef.current = null;
          onUploadStepEnd();
        }, AnimationTimings.SUCCESS_DISPLAY);
      }
      return;
    }

    // Cleanup timeout only if we're not in success state
    // This prevents clearing the timeout when transitioning from polling to success
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }
  }, [
    pollingStatus,
    apolloClient,
    closeDialog,
    hubble,
    language,
    zone,
    createProfileAvatarResponse,
    onUploadStepEnd,
  ]);

  // Initialize Lottie animation
  useEffect(() => {
    if (animationRef && animationRef.current) {
      animationInstance.current = lottie.loadAnimation({
        container: animationRef.current,
        renderer: 'svg',
        loop: true,
        autoplay: false,
        animationData,
      });
    }

    return function cleanup() {
      if (animationInstance.current) {
        animationInstance.current.destroy();
      }
    };
  }, []);

  // Determine error states
  const hasPollingUnexpectedError =
    // Polling stopped unexpectedly (network error, Apollo error, etc.)
    // Only trigger this check after polling has actually started AND we have a successful avatar creation
    createProfileAvatarResponse?.__typename === 'CreateProfileAvatarSuccess' &&
    hasStartedPollingRef.current &&
    !isPolling &&
    pollingStatus === undefined &&
    !pollingTimeout;

  const hasAnyError =
    createProfileAvatarError || // Avatar creation mutation failed
    uploadError || // S3 file upload failed
    pollingTimeout || // Polling reached max attempts
    profileAvatarStatusFailureError !== undefined || // Polling returned failure status with error details
    hasPollingUnexpectedError; // Polling stopped unexpectedly without error status

  // Sync error state to the ref so parent can access it
  useEffect(() => {
    hasError.current = hasAnyError;
    if (hasAnyError) {
      onUploadStepEnd();
    }
  }, [hasAnyError, hasError, onUploadStepEnd]);

  // Synchronized animation and text progression
  useEffect(() => {
    // Animation should play when we have a file and haven't reached success or error state
    const shouldPlayAnimation =
      croppedImage !== null &&
      pollingStatus !== 'ProfileAvatarStatusSuccess' &&
      !hasAnyError;

    // Only start the animation sequence once
    if (shouldPlayAnimation && !animationStartedRef.current) {
      animationStartedRef.current = true;

      // Initialize: UPLOADING animation + first progress state
      let progressIndex = 0;
      setAnimationState(AnimationState.UPLOADING);
      setUploadProgressLabel(UploadProgressStates[progressIndex]);

      // Progress to next message every 1.7 seconds
      intervalRef.current = setInterval(() => {
        progressIndex++;

        if (progressIndex < UploadProgressStates.length) {
          // Update text message
          setUploadProgressLabel(UploadProgressStates[progressIndex]);

          // Transition to PROCESSING animation at 1.7s mark
          if (progressIndex === 1) {
            setAnimationState(AnimationState.PROCESSING);
          }
        } else if (intervalRef.current) {
          // Reached final message, stop interval
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }, AnimationTimings.UPLOADING);
    }

    // Stop animation when we reach success or error state
    if (!shouldPlayAnimation && animationStartedRef.current) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      animationStartedRef.current = false;
    }
  }, [croppedImage, pollingStatus, hasAnyError]);

  const getUploadStatusLabel = () => {
    switch (uploadProgressLabel) {
      case UploadState.UPLOADING:
        return t('Uploading your photo');
      case UploadState.GETTING_READY:
        return t('Getting it ready');
      case UploadState.FINISHING_UP:
        return t('Finishing up');
      case UploadState.STILL_WORKING:
        return t('Still working');
      default:
        return t('Uploading your photo');
    }
  };

  // Run animation lifecycle
  useEffect(() => {
    runAvatarAnimationLifecycle({
      isSuccess: pollingStatus === 'ProfileAvatarStatusSuccess',
      animationState,
      setAnimationState,
      animationInstance,
    });
  }, [pollingStatus, animationState]);

  // Play animation based on state
  useEffect(() => {
    if (animationState && animationInstance.current) {
      playAnimation({ animationState, animationInstance });
    }
  }, [animationState]);

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = null;
      }
    },
    [],
  );

  // Track error analytics and metrics. Module-level flag ensures we count once per attempt:
  // the component can remount (e.g. React Strict Mode) and the effect would run again with the same deps.
  useEffect(() => {
    if (hasAnyError && !hasTrackedUploadError) {
      const errorTitle =
        profileAvatarStatusFailureError?.title ||
        'Something went wrong. Please try again or check back later.';

      metrics.count('avatar_upload_error', [
        `error_type:${pollingTimeout || hasPollingUnexpectedError ? 'timeout' : errorTitle}`,
      ]);

      hubble.trackEvent('component_viewed', {
        currentPageSection: 'photo preview',
        component: {
          type: 'card',
          name: 'avatar upload error',
          text: errorTitle,
        },
        displayLanguage: language,
        zone,
      });
      hasTrackedUploadError = true;
    }
  }, [
    hasAnyError,
    createProfileAvatarError,
    uploadError,
    pollingTimeout,
    profileAvatarStatusFailureError,
    hasPollingUnexpectedError,
    hubble,
    language,
    zone,
  ]);

  return hasAnyError ? (
    <UploadError
      profileAvatarStatusFailureError={profileAvatarStatusFailureError}
    />
  ) : (
    <Stack space="large" align="center">
      <Box
        borderRadius="large"
        display="flex"
        justifyContent="center"
        alignItems="center"
        style={{
          width: 256,
          height: 256,
        }}
      >
        <Stack space="large" align="center">
          <Box
            marginBottom="medium"
            style={{
              height: 'auto',
            }}
          >
            <Box ref={animationRef} className={styles.animation} />
          </Box>
          <Text size="large" align="center">
            {pollingStatus === 'ProfileAvatarStatusSuccess'
              ? t('Photo uploaded')
              : getUploadStatusLabel()}
          </Text>
        </Stack>
      </Box>
    </Stack>
  );
};
